import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RecordVoiceOver as VoiceIcon
} from '@mui/icons-material';
import api from '../../services/api';

const VoiceManager = () => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({
    open: false,
    voice: null,
    isEditing: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadVoices();
  }, []);

  const loadVoices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform/voices');
      setVoices(response.data);
    } catch (err) {
      console.error('Error loading voices:', err);
      setError('Failed to load voice models');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setDialog({
      open: true,
      voice: { id: '', name: '', description: '', gender: 'neutral' },
      isEditing: false
    });
  };

  const openEditDialog = (voice) => {
    setDialog({
      open: true,
      voice: { ...voice },
      isEditing: true
    });
  };

  const closeDialog = () => {
    setDialog({ open: false, voice: null, isEditing: false });
    setError('');
  };

  const saveVoice = async () => {
    try {
      setSaving(true);
      setError('');

      if (!dialog.voice.id.trim() || !dialog.voice.name.trim()) {
        setError('ID and Name are required');
        return;
      }

      const method = dialog.isEditing ? 'put' : 'post';
      const url = dialog.isEditing 
        ? `/platform/voices/${dialog.voice.id}`
        : '/platform/voices';

      await api[method](url, dialog.voice);

      closeDialog();
      loadVoices();
    } catch (err) {
      console.error('Error saving voice:', err);
      setError(err.response?.data?.message || 'Failed to save voice model');
    } finally {
      setSaving(false);
    }
  };

  const deleteVoice = async (voiceId) => {
    if (!window.confirm('Are you sure you want to delete this voice model? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/platform/voices/${voiceId}`);
      loadVoices();
    } catch (err) {
      console.error('Error deleting voice:', err);
      setError('Failed to delete voice model');
    }
  };

  const getVoiceColor = (voiceId) => {
    const colors = {
      'alloy': '#3498db',
      'ash': '#95a5a6',
      'ballad': '#e74c3c',
      'coral': '#f39c12',
      'echo': '#9b59b6',
      'sage': '#27ae60',
      'shimmer': '#f1c40f',
      'verse': '#34495e'
    };
    return colors[voiceId] || '#95a5a6';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Voice Models Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ 
            bgcolor: '#16a085',
            '&:hover': { bgcolor: '#1abc9c' }
          }}
        >
          Add Voice Model
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage OpenAI Realtime API voice models that can be used for cases.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <List>
          {voices.map((voice, index) => (
            <React.Fragment key={voice.id}>
              <ListItem>
                <Avatar
                  sx={{ 
                    bgcolor: getVoiceColor(voice.id),
                    mr: 2
                  }}
                >
                  <VoiceIcon />
                </Avatar>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <span>{voice.name}</span>
                      <Box 
                        component="span" 
                        sx={{ 
                          ml: 1,
                          px: 0.5, 
                          py: 0.25, 
                          borderRadius: 0.5, 
                          fontSize: '0.7rem',
                          bgcolor: voice.gender === 'male' ? '#e3f2fd' : 
                                   voice.gender === 'female' ? '#fce4ec' : '#f5f5f5',
                          color: voice.gender === 'male' ? '#1565c0' : 
                                 voice.gender === 'female' ? '#c2185b' : '#666'
                        }}
                      >
                        {voice.gender === 'male' ? '♂' : 
                         voice.gender === 'female' ? '♀' : '⚬'}
                      </Box>
                    </Box>
                  }
                  secondary={voice.description}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => openEditDialog(voice)}
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => deleteVoice(voice.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {index < voices.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>

        {voices.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No voice models available
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add voice models for use in cases
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              Add Your First Voice Model
            </Button>
          </Box>
        )}
      </Paper>

      <Box sx={{ mt: 4 }}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Note:</strong> Voice models are used to generate speech for virtual patients during conversations.
            Make sure the voice IDs match those available in OpenAI's Realtime API.
          </Typography>
        </Alert>
      </Box>

      {/* Voice Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.isEditing ? 'Edit Voice Model' : 'Add New Voice Model'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Voice ID"
            value={dialog.voice?.id || ''}
            onChange={(e) => setDialog({
              ...dialog,
              voice: { ...dialog.voice, id: e.target.value }
            })}
            required
            sx={{ mt: 2, mb: 2 }}
            helperText="OpenAI voice ID (e.g., alloy, ash, ballad)"
            disabled={dialog.isEditing}
          />
          
          <TextField
            fullWidth
            label="Display Name"
            value={dialog.voice?.name || ''}
            onChange={(e) => setDialog({
              ...dialog,
              voice: { ...dialog.voice, name: e.target.value }
            })}
            required
            sx={{ mb: 2 }}
            helperText="Human-readable name"
          />
          
          <TextField
            fullWidth
            label="Description"
            value={dialog.voice?.description || ''}
            onChange={(e) => setDialog({
              ...dialog,
              voice: { ...dialog.voice, description: e.target.value }
            })}
            multiline
            rows={2}
            helperText="Brief description of the voice characteristics"
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Gender</InputLabel>
            <Select
              value={dialog.voice?.gender || 'neutral'}
              label="Gender"
              onChange={(e) => setDialog({
                ...dialog,
                voice: { ...dialog.voice, gender: e.target.value }
              })}
            >
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="neutral">Neutral</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button 
            onClick={saveVoice} 
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : (dialog.isEditing ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VoiceManager;